/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Coast Terminal — Real Estate Intelligence Dashboard
 *
 * Bloomberg-style terminal for multifamily + mixed-use intelligence.
 * Layout:
 *   1. Live rate strip (always visible at top)
 *   2. Market selector sidebar
 *   3. 6-metric card grid for selected MSA
 *   4. AI query box with live data context
 *   5. Saved analyses panel (existing component)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import SavedAnalysesPanel from '../components/SavedAnalysesPanel';
import NewsFeed from '../components/NewsFeed';
import Comps from '../components/Comps';
import MacroDashboard from '../components/MacroDashboard';
import DealAlerts from '../components/DealAlerts';

const API = 'https://coast-terminal-production.up.railway.app';

interface TerminalProps {
  token: string;
}

interface Rates {
  treasury_10y: number;
  treasury_2y: number;
  fed_funds: number;
  sofr: number;
  mortgage_30y: number;
  cpi_yoy: number;
  unemployment: number;
  source: string;
  updated_at: string;
}

interface MarketData {
  msa: string;
  cap_rate: number | null;
  cap_rate_source: string | null;
  yoy_rent_growth: number | null;
  vacancy_rate: number | null;
  units_under_construction: number | null;
  median_rent: number | null;
  employment_yoy: number | null;
  fmr_1br: number | null;
  fmr_2br: number | null;
  sources: Record<string, string>;
  updated_at: string;
}

const MSAS = [
  'Atlanta, GA', 'Charlotte, NC', 'Dallas, TX', 'Denver, CO',
  'Seattle, WA', 'Kansas City, MO', 'Las Vegas, NV', 'Tampa, FL',
  'Washington, D.C.', 'Los Angeles, CA',
];

const Terminal: React.FC<TerminalProps> = ({ token }) => {
  const gold = '#b8956a';
  const goldDim = 'rgba(201,169,110,0.10)';
  const goldBorder = 'rgba(201,169,110,0.28)';
  const green = '#1A7A4A';
  const red = '#CC2200';
  const blue = '#1A5A8A';
  const bg = '#ffffff';
  const surface = '#fafaf8';
  const surfaceRaised = '#f2ede6';
  const border = '#e6ddd4';
  const borderLight = '#e6ddd4';
  const textPrimary = '#0d3d5c';
  const textSecondary = '#12526e';
  const textMuted = '#2d7fa0';
  const serifFont = "'Fraunces', Georgia, serif";
  const bodyFont = "'EB Garamond', Georgia, serif";

  const headers = { Authorization: `Bearer ${token}` };

  const [rates, setRates] = useState<Rates | null>(null);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [selectedMsa, setSelectedMsa] = useState('Tampa, FL');
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [marketLoading, setMarketLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [queryResult, setQueryResult] = useState('');
  const [queryLoading, setQueryLoading] = useState(false);
  const [allMarkets, setAllMarkets] = useState<MarketData[]>([]);
  const [view, setView] = useState<'market' | 'compare' | 'comps' | 'macro' | 'alerts'>('market');
  const [yieldCurve, setYieldCurve] = useState<any>(null);
  const [creIndicators, setCreIndicators] = useState<any>(null);
  const [censusData, setCensusData] = useState<any>(null);
  const [sentimentData, setSentimentData] = useState<any>(null);
  const queryRef = useRef<HTMLTextAreaElement>(null);

  // ── Fetch live rates ────────────────────────────────────────────────────────
  const fetchRates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/terminal/rates`, { headers });
      setRates(res.data);
    } catch {
      // Silently fail — rates are non-critical
    } finally {
      setRatesLoading(false);
    }
  }, []); // eslint-disable-line

  // ── Fetch market data for selected MSA ─────────────────────────────────────
  const fetchMarket = useCallback(async (msa: string) => {
    setMarketLoading(true);
    setMarketData(null);
    try {
      const encoded = encodeURIComponent(msa);
      const res = await axios.get(`${API}/terminal/market/${encoded}`, { headers });
      setMarketData(res.data);
    } catch {
      // Keep previous data on error
    } finally {
      setMarketLoading(false);
    }
  }, []); // eslint-disable-line

  // ── Fetch all markets for compare view ─────────────────────────────────────
  const fetchAllMarkets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/terminal/markets/all`, { headers });
      setAllMarkets(res.data.markets || []);
    } catch {}
  }, []); // eslint-disable-line

  // ── Run AI terminal query ───────────────────────────────────────────────────
  const runQuery = async () => {
    if (!query.trim() || queryLoading) return;
    setQueryLoading(true);
    setQueryResult('');
    try {
      const res = await axios.post(`${API}/terminal/query`, {
        query: query.trim(),
        msa: selectedMsa,
        include_rates: true,
      }, { headers });
      setQueryResult(res.data.response);
    } catch {
      setQueryResult('Query failed. Please try again.');
    } finally {
      setQueryLoading(false);
    }
  };

  useEffect(() => { fetchRates(); }, [fetchRates]);
  useEffect(() => { fetchMarket(selectedMsa); }, [selectedMsa, fetchMarket]);
  useEffect(() => { fetchAllMarkets(); }, [fetchAllMarkets]);

  // Fetch yield curve, CRE indicators, census, sentiment on load / MSA change
  useEffect(() => {
    const encoded = encodeURIComponent(selectedMsa);
    axios.get(`${API}/terminal/yield-curve`, { headers }).then(r => setYieldCurve(r.data)).catch(() => {});
    axios.get(`${API}/terminal/cre-indicators`, { headers }).then(r => setCreIndicators(r.data)).catch(() => {});
    axios.get(`${API}/terminal/census/${encoded}`, { headers }).then(r => setCensusData(r.data)).catch(() => {});
    axios.get(`${API}/terminal/news/sentiment-summary?msa=${encoded}`, { headers }).then(r => setSentimentData(r.data)).catch(() => {});
  }, [selectedMsa]); // eslint-disable-line

  // ── Render rate strip HERO ──────────────────────────────────────────────────
  const RateStrip = () => {
    if (ratesLoading) return (
      <div className="rate-strip-hero">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 20px', borderRight: `1px solid ${border}` }}>
            <div className="skeleton" style={{ width: '40px', height: '8px' }} />
            <div className="skeleton" style={{ width: '60px', height: '20px' }} />
          </div>
        ))}
      </div>
    );
    if (!rates) return null;

    const rateItems = [
      { label: '10Y TSY', value: rates.treasury_10y, suffix: '%', compare: 4.5, highlight: true },
      { label: '2Y TSY',  value: rates.treasury_2y,  suffix: '%', compare: 5.0, highlight: false },
      { label: '30Y MTG', value: rates.mortgage_30y,  suffix: '%', compare: 7.0, highlight: true },
      { label: 'FED',     value: rates.fed_funds,     suffix: '%', compare: 5.3, highlight: false },
      { label: 'SOFR',    value: rates.sofr,          suffix: '%', compare: 5.3, highlight: false },
      { label: 'CPI',     value: rates.cpi_yoy,       suffix: '%', compare: 3.5, highlight: false },
      { label: 'UNEMP',   value: rates.unemployment,  suffix: '%', compare: 4.0, highlight: false },
    ];

    const spread = yieldCurve?.current_spread;
    const inverted = yieldCurve?.inverted;

    return (
      <div className="rate-strip-hero">
        {/* Live indicator */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', paddingRight: '20px', borderRight: `1px solid ${border}`, marginRight: '4px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div className="live-dot-ring" style={{ width: '8px', height: '8px', flexShrink: 0 }} />
            <span style={{ fontSize: '8px', color: 'rgba(245,240,232,0.75)', letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase', fontFamily: 'sans-serif' }}>Live</span>
          </div>
          <span style={{ fontSize: '9px', color: 'rgba(245,240,232,0.55)', fontFamily: "'EB Garamond', Georgia, serif", fontStyle: 'italic' }}>
            {new Date(rates.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>

        {/* Rate items */}
        {rateItems.map(({ label, value, suffix, compare, highlight }) => {
          const isHigh = value > compare;
          const changeColor = (label === 'CPI' || label === 'UNEMP')
            ? (isHigh ? red : green)
            : (isHigh ? red : green);
          return (
            <div key={label} className="rate-item">
              <span className="rate-label">{label}</span>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '5px' }}>
                <span className={`rate-value ${highlight ? 'gold' : ''}`} style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                  {value?.toFixed(2)}{suffix}
                </span>
                <span className="rate-change" style={{ color: changeColor, fontFamily: 'sans-serif' }}>
                  {isHigh ? '▲' : '▼'}
                </span>
              </div>
            </div>
          );
        })}

        {/* 10Y-2Y Spread */}
        {spread !== null && spread !== undefined && (
          <div className="rate-item" style={{ borderLeft: `1px solid ${border}` }}>
            <span className="rate-label">10Y-2Y Spread</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span className="rate-value" style={{ color: inverted ? red : green, fontFamily: "'Fraunces', Georgia, serif" }}>
                {spread > 0 ? '+' : ''}{spread.toFixed(2)}%
              </span>
              {inverted && (
                <span className="inverted-badge">INVERTED</span>
              )}
            </div>
          </div>
        )}

        {/* Source */}
        <div style={{ marginLeft: 'auto', paddingLeft: '16px', flexShrink: 0 }}>
          <span style={{ fontSize: '8px', color: 'rgba(245,240,232,0.55)', letterSpacing: '1.5px', fontFamily: 'sans-serif', textTransform: 'uppercase' }}>
            {rates.source === 'fred' ? 'FRED · Federal Reserve' : 'Estimated'}
          </span>
        </div>
      </div>
    );
  };

  // ── Metric card ─────────────────────────────────────────────────────────────
  const MetricCard = ({ label, value, sub, trend, source, big = false }: {
    label: string; value: string | null; sub?: string; trend?: 'up' | 'down' | 'neutral';
    source?: string; big?: boolean;
  }) => (
    <div style={{ backgroundColor: '#ffffff', border: `1px solid ${border}`, padding: '18px 20px', position: 'relative', overflow: 'hidden', transition: 'border-color 0.2s' }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = borderLight)}
      onMouseLeave={e => (e.currentTarget.style.borderColor = border)}>
      <div style={{ position: 'absolute', top: 0, left: 0, width: '2px', height: '100%', backgroundColor: trend === 'up' ? green : trend === 'down' ? red : gold, opacity: 0.6 }} />
      <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>{label}</div>
      {value === null ? (
        <div style={{ height: '28px', width: '70%', backgroundColor: '#fafaf8', borderRadius: '1px', animation: 'pulse 1.5s ease-in-out infinite' }} />
      ) : (
        <div style={{ fontSize: big ? '28px' : '22px', fontWeight: 700, color: gold, lineHeight: 1, fontFamily: serifFont, letterSpacing: '-0.5px' }}>
          {value}
          {trend && <span style={{ fontSize: '12px', marginLeft: '6px', color: trend === 'up' ? green : trend === 'down' ? red : textMuted }}>{trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'}</span>}
        </div>
      )}
      {sub && <div style={{ fontSize: '10px', color: textSecondary, marginTop: '5px' }}>{sub}</div>}
      {source && <div style={{ fontSize: '8px', color: textMuted, marginTop: '4px', letterSpacing: '1px' }}>{source}</div>}
    </div>
  );

  // ── Render analysis (reuse existing pattern) ────────────────────────────────
  const renderAnalysis = (text: string) => {
    const serif = serifFont;
    return text.split('\n').map((line, i) => {
      const t = line.trim();
      if (!t) return <div key={i} style={{ height: '8px' }} />;
      if (t.startsWith('## ') || t.startsWith('# '))
        return <div key={i} style={{ fontSize: '10px', fontWeight: 800, color: gold, letterSpacing: '2px', textTransform: 'uppercase', marginTop: '24px', marginBottom: '8px', paddingBottom: '6px', borderBottom: `1px solid ${border}` }}>{t.replace(/^#+\s/, '')}</div>;
      if (t.startsWith('**') && t.endsWith('**'))
        return <div key={i} style={{ fontSize: '13px', fontWeight: 700, color: textPrimary, marginTop: '12px', marginBottom: '4px', fontFamily: serif }}>{t.replace(/\*\*/g, '')}</div>;
      if (t.startsWith('- ') || t.startsWith('• '))
        return <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '5px' }}>
          <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: gold, marginTop: '8px', flexShrink: 0 }} />
          <div style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.75, fontFamily: serif }} dangerouslySetInnerHTML={{ __html: t.replace(/^[-•]\s/, '').replace(/\*\*([^*]+)\*\*/g, `<strong style="color:${textPrimary};font-weight:700">$1</strong>`) }} />
        </div>;
      if (t.startsWith('|')) {
        // Table row
        const cells = t.split('|').filter(c => c.trim()).map(c => c.trim());
        return <div key={i} style={{ display: 'grid', gridTemplateColumns: `repeat(${cells.length}, 1fr)`, borderBottom: `1px solid ${border}`, padding: '6px 0' }}>
          {cells.map((cell, ci) => <div key={ci} style={{ fontSize: '12px', color: ci === 0 ? textPrimary : textSecondary, fontFamily: serif, padding: '0 8px' }}>{cell}</div>)}
        </div>;
      }
      return <div key={i} style={{ fontSize: '13px', color: textSecondary, lineHeight: 1.8, marginBottom: '3px', fontFamily: serif }}
        dangerouslySetInnerHTML={{ __html: t.replace(/\*\*([^*]+)\*\*/g, `<strong style="color:${textPrimary};font-weight:700">$1</strong>`) }} />;
    });
  };

  // ── Format helpers ──────────────────────────────────────────────────────────
  const fmt = {
    pct:  (v: number | null) => v !== null ? `${v.toFixed(1)}%` : null,
    rent: (v: number | null) => v !== null ? `$${Math.round(v).toLocaleString()}` : null,
    units: (v: number | null) => v !== null ? v.toLocaleString() : null,
    trend: (v: number | null): 'up' | 'down' | 'neutral' => v === null ? 'neutral' : v > 0 ? 'up' : v < 0 ? 'down' : 'neutral',
  };

  const SUGGESTED_QUERIES = [
    `What's the rent growth outlook for ${selectedMsa.split(',')[0]}?`,
    'Which markets have the best cap rate vs Treasury spread?',
    'Where is new supply creating headwinds for multifamily?',
    'What does the current rate environment mean for deals?',
    'Compare vacancy trends across target markets',
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* Rate Strip */}
      <RateStrip />

      {/* Main terminal body */}
      <div style={{ display: 'flex', gap: '0', flex: 1, minHeight: 0 }}>

        {/* ── Left sidebar: MSA selector ── */}
        <div style={{ width: '200px', flexShrink: 0, borderRight: `1px solid ${border}`, backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${border}`, backgroundColor: '#fafaf8' }}>
            <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '2px' }}>Markets</div>
            <div style={{ fontSize: '10px', color: textSecondary }}>Market Universe</div>
          </div>

          {/* View toggle */}
          <div style={{ display: 'flex', flexDirection: 'column', padding: '8px', gap: '4px', borderBottom: `1px solid ${border}` }}>
            {(['market', 'compare', 'comps', 'macro', 'alerts'] as const).map(v => (
              <button key={v} onClick={() => setView(v)} style={{ width: '100%', padding: '6px 0', border: 'none', cursor: 'pointer', fontSize: '8px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', fontFamily: 'inherit', backgroundColor: view === v ? 'rgba(184,149,106,0.15)' : 'transparent', color: view === v ? gold : textMuted, borderRadius: '1px', transition: 'all 0.15s', textAlign: 'left', paddingLeft: '8px' }}>
                {v === 'macro' ? 'Macro' : v === 'comps' ? 'Comps' : v === 'compare' ? 'Compare' : v === 'alerts' ? 'Alerts' : 'Market'}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {MSAS.map(msa => {
              const isSelected = selectedMsa === msa;
              const mktData = allMarkets.find(m => m.msa === msa);
              return (
                <button key={msa} onClick={() => { setSelectedMsa(msa); setView('market'); }}
                  style={{ width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', cursor: 'pointer', fontFamily: 'inherit', backgroundColor: isSelected ? goldDim : 'transparent', borderLeft: `2px solid ${isSelected ? gold : 'transparent'}`, transition: 'all 0.15s' }}>
                  <div style={{ fontSize: '11px', fontWeight: isSelected ? 700 : 500, color: isSelected ? gold : textSecondary }}>
                    {msa.split(',')[0]}
                  </div>
                  <div style={{ fontSize: '8px', color: textMuted, marginTop: '2px' }}>{msa.split(',')[1]?.trim()}</div>
                  {mktData?.cap_rate && (
                    <div style={{ fontSize: '9px', color: isSelected ? gold : textMuted, marginTop: '3px', fontWeight: 600 }}>
                      {mktData.cap_rate}% cap
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Main content area ── */}
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', minWidth: 0 }}>

          {view === 'market' && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>

              {/* Market header */}
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${border}`, backgroundColor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: '8px', color: gold, letterSpacing: '3px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>
                    Multifamily · Mixed-Use
                  </div>
                  <div style={{ fontSize: '22px', fontWeight: 400, color: textPrimary, fontFamily: serifFont, letterSpacing: '-0.3px' }}>
                    {selectedMsa}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {marketData?.updated_at && (
                    <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '1px' }}>
                      Updated {new Date(marketData.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                  <div style={{ fontSize: '8px', color: textMuted, marginTop: '2px', letterSpacing: '1px' }}>
                    {marketData?.sources?.rent === 'zillow_zori' ? '✓ Zillow ZORI' : 'Est. Data'}
                  </div>
                </div>
              </div>

              {/* 6-metric grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', backgroundColor: border, margin: '0' }}>
                <MetricCard
                  label="Cap Rate"
                  value={fmt.pct(marketData?.cap_rate ?? null)}
                  sub={marketData ? `${((marketData.cap_rate || 0) - (rates?.treasury_10y || 4.4)).toFixed(2)}% spread to 10Y` : undefined}
                  trend={marketData?.cap_rate ? (marketData.cap_rate > 5.0 ? 'up' : 'down') : 'neutral'}
                  source={marketData?.cap_rate_source || 'Research'}
                  big
                />
                <MetricCard
                  label="Median Rent (ZORI)"
                  value={fmt.rent(marketData?.median_rent ?? null)}
                  sub={marketData?.yoy_rent_growth !== null ? `${marketData?.yoy_rent_growth?.toFixed(1)}% YoY` : 'YoY loading...'}
                  trend={fmt.trend(marketData?.yoy_rent_growth ?? null)}
                  source="Zillow Research"
                  big
                />
                <MetricCard
                  label="YoY Rent Growth"
                  value={fmt.pct(marketData?.yoy_rent_growth ?? null)}
                  trend={fmt.trend(marketData?.yoy_rent_growth ?? null)}
                  source="Zillow ZORI"
                  big
                />
                <MetricCard
                  label="Vacancy Rate"
                  value={fmt.pct(marketData?.vacancy_rate ?? null)}
                  trend={marketData?.vacancy_rate ? (marketData.vacancy_rate > 7 ? 'down' : 'up') : 'neutral'}
                  source="Research"
                />
                <MetricCard
                  label="New Supply Pipeline"
                  value={fmt.units(marketData?.units_under_construction ?? null)}
                  sub="Units permitted / 12 months"
                  trend={marketData?.units_under_construction ? (marketData.units_under_construction > 3000 ? 'down' : 'neutral') : 'neutral'}
                  source="U.S. Census BPS"
                />
                <MetricCard
                  label="Employment Growth"
                  value={fmt.pct(marketData?.employment_yoy ?? null)}
                  sub="Total nonfarm, YoY"
                  trend={fmt.trend(marketData?.employment_yoy ?? null)}
                  source="Bureau of Labor Statistics"
                />
              </div>

              {/* HUD FMR strip — only when available */}
              {(marketData?.fmr_1br || marketData?.fmr_2br) && (
                <div style={{ backgroundColor: '#ffffff', borderBottom: `1px solid ${border}`, padding: '10px 20px', display: 'flex', gap: '24px', alignItems: 'center' }}>
                  <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>HUD FAIR MARKET RENTS</div>
                  {marketData.fmr_1br && <div style={{ fontSize: '12px', color: textSecondary }}>Studio: <strong style={{ color: textPrimary }}>${marketData.fmr_1br}</strong></div>}
                  {marketData.fmr_2br && <div style={{ fontSize: '12px', color: textSecondary }}>1BR: <strong style={{ color: textPrimary }}>${marketData.fmr_1br}</strong> · 2BR: <strong style={{ color: textPrimary }}>${marketData.fmr_2br}</strong></div>}
                  <div style={{ fontSize: '8px', color: textMuted, marginLeft: 'auto' }}>HUD.gov</div>
                </div>
              )}

              {/* Sentiment indicator */}
              {sentimentData && sentimentData.total > 0 && (
                <div style={{ backgroundColor: '#ffffff', borderBottom: `1px solid ${border}`, padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>NEWS SENTIMENT (7D)</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 800, color: sentimentData.sentiment === 'bullish' ? green : sentimentData.sentiment === 'bearish' ? red : textMuted }}>
                      {sentimentData.sentiment === 'bullish' ? '▲ BULLISH' : sentimentData.sentiment === 'bearish' ? '▼ BEARISH' : '— NEUTRAL'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', marginLeft: '8px' }}>
                    <span style={{ fontSize: '10px', color: green }}>{sentimentData.bullish} bullish</span>
                    <span style={{ fontSize: '10px', color: red }}>{sentimentData.bearish} bearish</span>
                    <span style={{ fontSize: '10px', color: textMuted }}>{sentimentData.neutral} neutral</span>
                  </div>
                </div>
              )}

              {/* CRE macro indicators */}
              {creIndicators && Object.keys(creIndicators).length > 0 && (
                <div style={{ backgroundColor: '#ffffff', borderBottom: `1px solid ${border}` }}>
                  <div style={{ padding: '8px 20px', borderBottom: `1px solid ${border}`, fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>
                    CRE MACRO INDICATORS · FRED
                  </div>
                  <div style={{ display: 'flex', overflowX: 'auto' }}>
                    {Object.entries(creIndicators).map(([key, ind]: [string, any]) => (
                      <div key={key} style={{ padding: '12px 20px', borderRight: `1px solid ${border}`, minWidth: '160px', flexShrink: 0 }}>
                        <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '6px' }}>{ind.label}</div>
                        <div style={{ fontSize: '18px', fontWeight: 700, color: gold, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                          {ind.value?.toFixed(1)}<span style={{ fontSize: '11px', color: textMuted, fontFamily: 'inherit' }}> {ind.unit}</span>
                        </div>
                        <div style={{ fontSize: '9px', color: ind.change > 0 ? green : ind.change < 0 ? red : textMuted, marginTop: '3px' }}>
                          {ind.change > 0 ? '▲' : ind.change < 0 ? '▼' : '—'} {Math.abs(ind.change).toFixed(1)} vs prior · {ind.date}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Census demographics */}
              {censusData && (censusData.population || censusData.net_migration) && (
                <div style={{ backgroundColor: '#ffffff', borderBottom: `1px solid ${border}`, padding: '10px 20px', display: 'flex', gap: '32px', alignItems: 'center' }}>
                  <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>CENSUS {censusData.year}</div>
                  {censusData.population && (
                    <div>
                      <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '2px' }}>Population</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: textPrimary }}>{(censusData.population / 1e6).toFixed(2)}M</div>
                    </div>
                  )}
                  {censusData.net_migration !== null && censusData.net_migration !== undefined && (
                    <div>
                      <div style={{ fontSize: '8px', color: textMuted, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '2px' }}>Net Migration</div>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: censusData.net_migration > 0 ? green : red }}>
                        {censusData.net_migration > 0 ? '+' : ''}{censusData.net_migration?.toLocaleString()}
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: '9px', color: textMuted, marginLeft: 'auto' }}>U.S. Census Bureau</div>
                </div>
              )}
              <div style={{ padding: '20px 24px', backgroundColor: bg, borderTop: `1px solid ${border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: green, animation: 'pulse 2.5s ease-in-out infinite' }} />
                  <div style={{ fontSize: '9px', color: textMuted, letterSpacing: '2px', fontWeight: 700, textTransform: 'uppercase' }}>
                    Coast AI Terminal — {selectedMsa}
                  </div>
                </div>

                {/* Suggested queries */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  {SUGGESTED_QUERIES.map(q => (
                    <button key={q} onClick={() => { setQuery(q); queryRef.current?.focus(); }}
                      style={{ fontSize: '10px', color: textMuted, backgroundColor: '#fafaf8', border: `1px solid ${border}`, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit', borderRadius: '1px', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = goldBorder; e.currentTarget.style.color = gold; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = textMuted; }}>
                      {q}
                    </button>
                  ))}
                </div>

                {/* Query input */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, backgroundColor: '#fafaf8', border: `1px solid ${borderLight}`, borderRadius: '2px', display: 'flex', alignItems: 'center', padding: '0 12px', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: gold, fontFamily: 'monospace', flexShrink: 0 }}>{'>'}</span>
                    <textarea
                      ref={queryRef}
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runQuery(); } }}
                      placeholder={`Ask about ${selectedMsa.split(',')[0]} or any market...`}
                      rows={2}
                      style={{ flex: 1, backgroundColor: 'transparent', border: 'none', outline: 'none', color: textPrimary, fontSize: '13px', fontFamily: 'inherit', resize: 'none', lineHeight: 1.5, padding: '10px 0' }}
                    />
                  </div>
                  <button onClick={runQuery} disabled={queryLoading || !query.trim()}
                    style={{ backgroundColor: query.trim() && !queryLoading ? gold : surfaceRaised, color: query.trim() && !queryLoading ? bg : textMuted, border: 'none', padding: '0 20px', height: '52px', fontWeight: 800, fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', cursor: query.trim() && !queryLoading ? 'pointer' : 'default', fontFamily: 'inherit', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                    {queryLoading ? 'Analyzing...' : 'Run Query'}
                  </button>
                </div>

                {/* Query result */}
                {queryResult && (
                  <div style={{ marginTop: '16px', backgroundColor: '#ffffff', border: `1px solid ${border}`, borderLeft: `3px solid ${gold}`, padding: '20px 24px', animation: 'fadeUp 0.3s ease both' }}>
                    {renderAnalysis(queryResult)}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Macro view ── */}
          {view === 'macro' && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <MacroDashboard token={token} rates={rates} />
            </div>
          )}

          {/* ── Comps view ── */}
          {view === 'comps' && (
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Comps
                token={token}
                msa={selectedMsa}
              />
            </div>
          )}

          {/* ── Compare view ── */}
          {view === 'compare' && (
            <div style={{ padding: '20px 24px' }}>
              <div style={{ fontSize: '9px', color: gold, letterSpacing: '3px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '6px' }}>Market Comparison</div>
              <div style={{ fontSize: '20px', fontWeight: 400, color: textPrimary, fontFamily: serifFont, marginBottom: '20px' }}>All Target Markets</div>
              <div style={{ backgroundColor: '#ffffff', border: `1px solid ${border}`, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#fafaf8' }}>
                      {['Market', 'Cap Rate', 'Rent Growth', 'Vacancy', 'New Supply', 'Employment'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: '8px', color: textMuted, letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: 700, borderBottom: `1px solid ${border}`, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {allMarkets.sort((a, b) => (b.yoy_rent_growth || 0) - (a.yoy_rent_growth || 0)).map(m => (
                      <tr key={m.msa} onClick={() => { setSelectedMsa(m.msa); setView('market'); }}
                        style={{ borderBottom: `1px solid ${border}`, cursor: 'pointer', transition: 'background 0.1s' }}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = surfaceRaised)}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                        <td style={{ padding: '11px 14px', color: textPrimary, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          {m.msa.split(',')[0]}
                          <div style={{ fontSize: '9px', color: textMuted, marginTop: '1px' }}>{m.msa.split(',')[1]?.trim()}</div>
                        </td>
                        <td style={{ padding: '11px 14px', color: gold, fontWeight: 700 }}>{m.cap_rate ? `${m.cap_rate}%` : '—'}</td>
                        <td style={{ padding: '11px 14px', color: m.yoy_rent_growth && m.yoy_rent_growth > 0 ? green : red, fontWeight: 600 }}>
                          {m.yoy_rent_growth !== null ? `${m.yoy_rent_growth?.toFixed(1)}%` : '—'}
                        </td>
                        <td style={{ padding: '11px 14px', color: textSecondary }}>{m.vacancy_rate ? `${m.vacancy_rate}%` : '—'}</td>
                        <td style={{ padding: '11px 14px', color: textSecondary }}>{m.units_under_construction ? m.units_under_construction.toLocaleString() : '—'}</td>
                        <td style={{ padding: '11px 14px', color: m.employment_yoy && m.employment_yoy > 0 ? green : textSecondary }}>
                          {m.employment_yoy !== null ? `${m.employment_yoy?.toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {/* ── Alerts view ── */}
          {view === 'alerts' && (
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <DealAlerts token={token} />
            </div>
          )}
        </div>

        {/* ── Right sidebar: News + Saved analyses ── */}
        <div style={{ width: '300px', flexShrink: 0, borderLeft: `1px solid ${border}`, backgroundColor: '#ffffff', display: 'flex', flexDirection: 'column' }}>
          {/* News feed takes top 60% */}
          <div style={{ flex: '0 0 60%', borderBottom: `1px solid ${border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <NewsFeed token={token} msa={selectedMsa} />
          </div>
          {/* Saved analyses below */}
          <div style={{ flex: '0 0 40%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <SavedAnalysesPanel
              token={token}
              currentReport={queryResult}
              currentQuery={`${selectedMsa}: ${query}`}
              onLoadAnalysis={(content, q) => {
                setQueryResult(content);
                const parts = q.split(': ');
                if (parts.length > 1 && MSAS.includes(parts[0])) {
                  setSelectedMsa(parts[0]);
                  setQuery(parts.slice(1).join(': '));
                }
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Terminal;